// EditAdminProduct.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, FileText, Video, X } from "lucide-react";
import slugify from "slugify";
import { getMedia, uploadMedia } from "../../../services/admin/media";
import { getProductById, updateProduct } from "../../../services/admin/product";
import { ProductData, ProductRequest } from "../../../types/product";
import { getCategory } from "../../../services/admin/category";
import { getBrands } from "../../../services/admin/brand";
import RichTextEditor from "../../../Component/RickTextEditor";

interface MediaItem {
  id: number;
  filename: string;
  url: string;
}

interface Category {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

function EditAdminProduct() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ProductData>({
    product_name: "",
    product_slug: "",
    product_main_image: "",
    product_image: [],
    sku: "",
    product_size: "",
    short_description: "",
    download_more_info: "",
    download_datasheet: "",
    download_certificates: "",
    product_video: "",
    category_id: [],
    brand_id: 0,
    status: "publish",
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaForField, setMediaForField] = useState<
    "images"
    | "download_more_info"
    | "download_datasheet"      // new
    | "download_certificates"   // new
    | "product_video"
    | "product_main_image"
    | null
  >(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const [catSearch, setCatSearch] = useState("");

  // Fetch product, categories, brands
  useEffect(() => {
    async function init() {
      const [catRes, brandRes, productRes] = await Promise.all([
        getCategory(),
        getBrands(),
        getProductById(Number(id)),
      ]);

      if (catRes.success) setCategories(catRes.data);
      if (brandRes.success) setBrands(brandRes.data);

      if (productRes.success) {
        setFormData({
          ...productRes.data,
          category_id: Array.isArray(productRes.data.category_id)
            ? productRes.data.category_id
            : [productRes.data.category_id].filter(Boolean),
        });
      } else {
        setError("Failed to fetch product data.");
      }
    }
    init();
  }, [id]);

  useEffect(() => {
    if (showMediaModal) fetchMedia();
  }, [showMediaModal]);

  const fetchMedia = async () => {
    const res = await getMedia();
    if (res.success) setMediaList(res.data);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "product_name") {
      const slug = slugify(value, { lower: true, strict: true });
      setFormData((prev) => ({
        ...prev,
        product_name: value,
        product_slug: slug,
      }));
    } else if (name === "category_id") {
      const selected = Array.from(
        (e.target as HTMLSelectElement).selectedOptions
      ).map((opt) => opt.value); // string[]

      setFormData((prev) => ({
        ...prev,
        category_id: selected,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDescriptionChange = (content: string) =>
    setFormData((prev) => ({ ...prev, short_description: content }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (formData.product_image.length === 0) {
      setError("Please select at least one product image.");
      setLoading(false);
      return;
    }

    const payload: ProductRequest = {
      ...formData,
      brand_id: Number(formData.brand_id),
      category_id: formData.category_id,
    };

    const res = await updateProduct(Number(id), payload);

    if (res.success) navigate("/admin/product");
    else setError(res.error || "Failed to update product");

    setLoading(false);
  };

  const handleSelectMedia = (media: MediaItem) => {
    if (!mediaForField) return;
    const fullUrl = `${media.url}`;

    if (mediaForField === "images") {
      setFormData((prev) => {
        const exists = prev.product_image.includes(fullUrl);
        const updatedImages = exists
          ? prev.product_image.filter((img) => img !== fullUrl)
          : [...prev.product_image, fullUrl];
        return { ...prev, product_image: updatedImages };
      });
    } else if (mediaForField === "product_main_image") {
      setFormData((prev) => ({ ...prev, product_main_image: fullUrl }));
    } else if (mediaForField === "download_more_info") {
      setFormData((prev) => ({ ...prev, download_more_info: fullUrl }));
    } else if (mediaForField === "download_datasheet") {
      setFormData((prev) => ({ ...prev, download_datasheet: fullUrl }));
    } else if (mediaForField === "download_certificates") {
      setFormData((prev) => ({ ...prev, download_certificates: fullUrl }));
    } else if (mediaForField === "product_video") {
      setFormData((prev) => ({ ...prev, product_video: fullUrl }));
    }

    if (mediaForField !== "images") setShowMediaModal(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    setUploading(true);
    const res = await uploadMedia(Array.from(files));
    if (res.success) fetchMedia();
    setUploading(false);
  };

  useEffect(() => {
    async function init() {
      const [catRes, brandRes, productRes] = await Promise.all([
        getCategory(),
        getBrands(),
        getProductById(Number(id)),
      ]);

      if (catRes.success && brandRes.success && productRes.success) {
        // convert category_id to string[]
        const productData = {
          ...productRes.data,
          category_id: productRes.data.category_id.map(String),
        };
        setCategories(catRes.data);
        setBrands(brandRes.data);
        setFormData(productData);
      } else {
        setError("Failed to fetch data");
      }
    }
    init();
  }, [id]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">Edit Product</h1>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-600 hover:text-[#009FE3]"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col 2xl:flex-row gap-6">
        {/* Left */}
        <div className="flex-1 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-100 p-2 rounded">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name
            </label>
            <input
              type="text"
              name="product_name"
              value={formData.product_name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Slug
            </label>
            <input
              type="text"
              value={formData.product_slug}
              readOnly
              className="w-full px-4 py-2 border bg-gray-100 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <RichTextEditor
              initialData={formData.short_description}
              isInvalid={false}
              initialLoading={false}
              onChange={handleDescriptionChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
            <input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Sizes
            </label>
            <input
              type="text"
              name="product_size"
              value={formData.product_size}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
            />
          </div>

          <div className="flex flex-wrap -mx-4 gap-y-6">
            <div className="w-full max-w-full px-4">
              {/* Product Gallery */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Images
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMediaForField("images");
                    setShowMediaModal(true);
                  }}
                  className="w-full px-4 py-2 bg-white border rounded-lg"
                >
                  {formData.product_image.length
                    ? `Change Images (${formData.product_image.length})`
                    : "Select Images"}
                </button>

                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.product_image.map((url, idx) => (
                    <div
                      key={idx}
                      className="relative w-20 sm:w-40 h-20 sm:h-40 border rounded overflow-hidden"
                    >
                      <img
                        src={url}
                        alt={formData.product_name}
                        className="object-cover w-full h-full"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            product_image: prev.product_image.filter(
                              (img) => img !== url
                            ),
                          }))
                        }
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-1"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full sm:w-1/2 2xl:w-1/4 max-w-full px-4">
              {/* More Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  More Information
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMediaForField("download_more_info");
                    setShowMediaModal(true);
                  }}
                  className="w-full px-4 py-2 bg-white border rounded-lg"
                >
                  {formData.download_more_info ? "Change File" : "Select File"}
                </button>
                {formData.download_more_info && (
                  <div className="relative mt-2 w-full h-[200px] border rounded overflow-hidden">
                    {formData.download_more_info.toLowerCase().endsWith(".pdf") ? (
                      <iframe
                        src={formData.download_more_info}
                        className="w-full h-full"
                        title="More Info Preview"
                      ></iframe>
                    ) : (
                      <img
                        src={formData.download_more_info}
                        alt="Selected"
                        className="w-full h-full object-cover"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          download_more_info: "",
                        }))
                      }
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-2"
                      title="Remove file"
                    >
                      &times;
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="w-full sm:w-1/2 2xl:w-1/4 max-w-full px-4">
              {/* Video */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Video
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMediaForField("product_video");
                    setShowMediaModal(true);
                  }}
                  className="w-full px-4 py-2 bg-white border rounded-lg"
                >
                  {formData.product_video ? "Change Video" : "Select Video"}
                </button>
                {formData.product_video && (
                  <div className="relative w-full mt-2 h-[200px] rounded overflow-hidden">
                    <video
                      src={formData.product_video}
                      controls
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          product_video: "",
                        }))
                      }
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-2"
                      title="Remove video"
                    >
                      &times;
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Datasheet */}
            <div className="w-full sm:w-1/2 2xl:w-1/4 max-w-full px-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Download Datasheet
              </label>
              <button
                type="button"
                onClick={() => {
                  setMediaForField("download_datasheet");
                  setShowMediaModal(true);
                }}
                className="w-full px-4 py-2 bg-white border rounded-lg"
              >
                {formData.download_datasheet ? "Change File" : "Select File"}
              </button>

              {formData.download_datasheet && (
                <div className="relative mt-2 w-full h-[200px] border rounded overflow-hidden">
                  <iframe
                    src={formData.download_datasheet}
                    className="w-full h-full"
                    title="Datasheet Preview"
                  ></iframe>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        download_datasheet: "",
                      }))
                    }
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-2"
                    title="Remove file"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>

            {/* Certificates */}
            <div className="w-full sm:w-1/2 2xl:w-1/4 max-w-full px-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Download Certificates
              </label>
              <button
                type="button"
                onClick={() => {
                  setMediaForField("download_certificates");
                  setShowMediaModal(true);
                }}
                className="w-full px-4 py-2 bg-white border rounded-lg"
              >
                {formData.download_certificates ? "Change File" : "Select File"}
              </button>

              {formData.download_certificates && (
                <div className="relative mt-2 w-full h-[200px] border rounded overflow-hidden">
                  <iframe
                    src={formData.download_certificates}
                    className="w-full h-full"
                    title="Certificates Preview"
                  ></iframe>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        download_certificates: "",
                      }))
                    }
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-2"
                    title="Remove file"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="w-full 2xl:w-1/4 space-y-4">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
            >
              <option value="publish">Publish</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>

            {/* Dropdown button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCatDropdown((prev) => !prev)}
                className="w-full px-4 py-2 border rounded-lg flex justify-between items-center focus:ring-2 focus:ring-[#009FE3] bg-white"
              >
                {formData.category_id.length > 0
                  ? `${formData.category_id.length} selected`
                  : "Select Categories"}
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>

              {/* Dropdown options */}
              {showCatDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {/* Search input */}
                  <div className="sticky top-0 bg-white border-b p-2">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={catSearch}
                      onChange={(e) => setCatSearch(e.target.value)}
                      className="w-full px-3 py-1 border rounded-md focus:ring-1 focus:ring-[#009FE3] text-sm"
                    />
                  </div>

                  {/* Filtered categories */}
                  {categories
                    .filter((cat) =>
                      cat.name.toLowerCase().includes(catSearch.toLowerCase())
                    )
                    .map((cat) => {
                      const catIdStr = cat.id.toString();
                      const isChecked = formData.category_id.includes(catIdStr);
                      return (
                        <label
                          key={cat.id}
                          className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={catIdStr}
                            checked={isChecked}
                            onChange={() => {
                              setFormData((prev: any) => {
                                const exists = prev.category_id.includes(catIdStr);
                                return {
                                  ...prev,
                                  category_id: exists
                                    ? prev.category_id.filter((id: string) => id !== catIdStr)
                                    : [...prev.category_id, catIdStr],
                                };
                              });
                            }}
                            className="h-4 w-4 text-[#009FE3] border-gray-300 rounded"
                          />
                          <span>{cat.name}</span>
                        </label>
                      );
                    })}

                  {/* No results */}
                  {categories.filter((cat) =>
                    cat.name.toLowerCase().includes(catSearch.toLowerCase())
                  ).length === 0 && (
                      <p className="px-4 py-2 text-gray-500 text-sm">No results found</p>
                    )}
                </div>
              )}
            </div>

            {/* Pills for selected categories */}
            {formData.category_id.length > 0 && categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.category_id.map((id) => {
                  const category = categories.find((c) => c.id.toString() === id);
                  return (
                    <span
                      key={id}
                      className="flex items-center bg-[#009FE3]/10 text-[#009FE3] px-3 py-1 rounded-full text-sm"
                    >
                      {category ? category.name : "Unknown"}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev: any) => ({
                            ...prev,
                            category_id: prev.category_id.filter((cid: string) => cid !== id),
                          }))
                        }
                        className="ml-2 text-xs text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>


          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand
            </label>
            <select
              name="brand_id"
              value={formData.brand_id}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009FE3]"
            >
              <option value="">Select Brand</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Product Main Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main Image
            </label>
            <button
              type="button"
              onClick={() => {
                setMediaForField("product_main_image");
                setShowMediaModal(true);
              }}
              className="w-full px-4 py-2 bg-gray-100 border rounded-lg hover:bg-gray-200"
            >
              {formData.product_main_image ? "Change Main Image" : "Select Main Image"}
            </button>
            {formData.product_main_image && (
              <img
                src={formData.product_main_image}
                alt="Selected"
                className="mt-2 w-full border rounded"
              />
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 text-white rounded-lg ${loading
              ? "bg-[#009FE3]/50 cursor-not-allowed"
              : "bg-[#009FE3] hover:bg-[#008AC6]"
              }`}
          >
            {loading ? "Updating..." : "Update Product"}
          </button>
        </div>
      </form>

      {/* Media Modal */}
      {showMediaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[90%] max-w-3xl relative">
            <button
              onClick={() => setShowMediaModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-black"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold mb-4">Select {mediaForField}</h2>

            <input
              type="file"
              multiple={mediaForField === "images"}
              accept={
                mediaForField === "product_video"
                  ? "video/*"
                  : mediaForField === "download_more_info" || mediaForField === "download_datasheet" || mediaForField === "download_certificates"
                    ? ".pdf"
                    : "image/*"
              }
              onChange={handleFileUpload}
            />

            {uploading && <p className="text-sm text-gray-500 mb-2">Uploading...</p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-[300px] overflow-y-auto">
              {mediaList
                .filter((media) => {
                  const filename = media.filename.toLowerCase();

                  if (mediaForField === "images" || mediaForField === "product_main_image") {
                    return filename.match(/\.(jpg|jpeg|png|gif|webp)$/);
                  } else if (mediaForField === "product_video") {
                    return filename.match(/\.(mp4|webm|ogg)$/);
                  } else if (mediaForField === "download_more_info") {
                    return filename.match(/\.(jpg|jpeg|png|gif|webp|pdf)$/);
                  } else if (mediaForField === "download_datasheet" || mediaForField === "download_certificates") {
                    return filename.match(/\.pdf$/); // PDF only
                  }

                  return false;
                })
                .map((media) => (
                  <div
                    key={media.id}
                    className={`border rounded hover:shadow cursor-pointer relative ${mediaForField === "images" &&
                      formData.product_image.includes(media.url)
                      ? "ring-2 ring-[#009FE3]"
                      : ""
                      }`}
                    onClick={() => handleSelectMedia(media)}
                  >
                    {mediaForField === "product_video" ? (
                      <div className="w-full h-24 flex items-center justify-center bg-[#009FE3] rounded">
                        <Video size={40} className="text-white" />
                      </div>
                    ) : mediaForField === "download_more_info" || mediaForField === "download_datasheet" || mediaForField === "download_certificates" ? (
                      /\.(jpg|jpeg|png|gif|webp)$/i.test(media.filename) ? (
                        <img src={media.url} alt={media.filename} className="w-full h-28 object-cover rounded" />
                      ) : (
                        <div className="w-full h-28 flex items-center justify-center bg-[#009FE3] rounded">
                          <FileText size={40} className="text-white" />
                        </div>
                      )
                    ) : (
                      <img src={media.url} alt={media.filename} className="w-full h-28 object-cover rounded" />
                    )}
                    <p className="text-xs text-center mt-1 truncate">{media.filename}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditAdminProduct;
